import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { Helmet } from 'react-helmet-async';
import { cookieManager } from '../../utils/cookieManager';
import CRMLayout from './CRMLayout';

const Cookies = () => {
  const navigate = useNavigate();
  const [consentStatus, setConsentStatus] = useState({
    cookies: false,
    ads: false
  });

  useEffect(() => {
    // Check current consent status
    const cookieConsent = cookieManager.get('cookieConsent') === 'true';
    const adsConsent = cookieManager.get('adsConsent') === 'true';
    setConsentStatus({ cookies: cookieConsent, ads: adsConsent });
  }, []);

  const handleWithdrawConsent = () => {
    if (window.withdrawConsent) {
      window.withdrawConsent();
      setConsentStatus({ cookies: false, ads: false });
    }
  };

  const handleAcceptAll = () => {
    cookieManager.set('cookieConsent', 'true');
    cookieManager.set('adsConsent', 'true');
    setConsentStatus({ cookies: true, ads: true });

    // Trigger consent banner to reload services
    window.dispatchEvent(new StorageEvent('storage', {
      key: 'cookieConsent',
      newValue: 'true'
    }));
  };

  const handleBack = (e) => {
    e.preventDefault();
    navigate(-1);
  };

  // Get canonical URL based on hostname
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const isSubdomain = hostname === 'crm.livelovespain.com' || hostname.startsWith('crm.');
  const canonicalUrl = isSubdomain 
    ? 'https://crm.livelovespain.com/cookies'
    : 'https://www.livelovespain.com/crm/cookies';

  // Add JSON-LD schemas
  React.useEffect(() => {
    const baseUrl = isSubdomain ? 'https://crm.livelovespain.com' : 'https://www.livelovespain.com/crm';
    
    // Breadcrumbs Schema
    const breadcrumbsId = 'jsonld-breadcrumbs';
    let breadcrumbsScript = document.getElementById(breadcrumbsId);
    const breadcrumbsPayload = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": baseUrl + "/"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Cookie Policy",
          "item": canonicalUrl
        }
      ]
    };
    if (!breadcrumbsScript) {
      breadcrumbsScript = document.createElement('script');
      breadcrumbsScript.type = 'application/ld+json';
      breadcrumbsScript.id = breadcrumbsId;
      document.head.appendChild(breadcrumbsScript);
    }
    breadcrumbsScript.text = JSON.stringify(breadcrumbsPayload);

    // WebPage Schema
    const webpageId = 'jsonld-webpage';
    let webpageScript = document.getElementById(webpageId);
    const webpagePayload = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Cookie Policy - LiveLoveSpain CRM",
      "description": "LiveLoveSpain's Cookies Policy - Learn how we use cookies and manage your cookie preferences for a better browsing experience.",
      "url": canonicalUrl,
      "inLanguage": "en",
      "isPartOf": {
        "@type": "WebSite",
        "name": "LiveLoveSpain CRM",
        "url": baseUrl
      },
      "about": {
        "@type": "Thing",
        "name": "Cookie Policy"
      },
      "publisher": {
        "@type": "Organization",
        "name": "LiveLoveSpain"
      }
    };
    if (!webpageScript) {
      webpageScript = document.createElement('script');
      webpageScript.type = 'application/ld+json';
      webpageScript.id = webpageId;
      document.head.appendChild(webpageScript);
    }
    webpageScript.text = JSON.stringify(webpagePayload);

    return () => {
      const breadcrumbs = document.getElementById(breadcrumbsId);
      const webpage = document.getElementById(webpageId);
      if (breadcrumbs) breadcrumbs.remove();
      if (webpage) webpage.remove();
    };
  }, [canonicalUrl, isSubdomain]);

  return (
    <CRMLayout>
      <Helmet>
        <title>Cookie Policy - LiveLoveSpain CRM</title>
        <meta name="description" content="LiveLoveSpain's Cookies Policy - Learn how we use cookies and manage your cookie preferences for a better browsing experience." />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content="Cookie Policy - LiveLoveSpain CRM" />
        <meta property="og:description" content="LiveLoveSpain's Cookies Policy - Learn how we use cookies and manage your cookie preferences for a better browsing experience." />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Cookie Policy - LiveLoveSpain CRM" />
        <meta name="twitter:description" content="LiveLoveSpain's Cookies Policy - Learn how we use cookies and manage your cookie preferences for a better browsing experience." />
      </Helmet>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <button
          onClick={handleBack}
          className="inline-flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 mb-6"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Back
        </button>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
              Cookie Policy
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Last Updated: {new Date().toLocaleDateString()}
            </p>
          </div>

          <p className="text-gray-600 dark:text-gray-300 mb-8">
            LiveLoveSpain uses cookies and similar technologies to enhance your experience, analyze site usage, and assist in our marketing efforts. This policy explains how we use cookies and how you can manage your preferences.
          </p>

          {/* Consent Management Section */}
          <div className="mb-8 p-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Manage Your Cookie Preferences</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-700 dark:text-gray-300 font-medium">Current Status</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Cookies: <span className={consentStatus.cookies ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
                      {consentStatus.cookies ? 'Accepted' : 'Declined'}
                    </span>
                    {' • '}
                    Ads: <span className={consentStatus.ads ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
                      {consentStatus.ads ? 'Accepted' : 'Declined'}
                    </span>
                  </p>
                </div>
                <div className="flex gap-3">
                  {!consentStatus.cookies && (
                    <button
                      onClick={handleAcceptAll}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Accept All
                    </button>
                  )}
                  {consentStatus.cookies && (
                    <button
                      onClick={handleWithdrawConsent}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                    >
                      Withdraw Consent
                    </button>
                  )}
                </div>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                You can withdraw your consent at any time. Withdrawing consent will disable tracking cookies and personalized ads, but essential cookies required for the site to function will remain active.
              </p>
            </div>
          </div>

          {/* Cookie Types Section */}
          <div className="space-y-8">
            <section>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Types of Cookies We Use</h2>
              
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">Essential Cookies</h3>
                  <p className="text-gray-600 dark:text-gray-300">
                    These cookies are necessary for the website to function properly. They enable basic features like page navigation, secure login, and access to protected areas. These cookies cannot be disabled.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">Analytics Cookies</h3>
                  <p className="text-gray-600 dark:text-gray-300">
                    We use analytics cookies to understand how visitors interact with our website. These cookies help us improve our services by collecting information about page views, time spent on pages, and user interactions.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">Advertising Cookies</h3>
                  <p className="text-gray-600 dark:text-gray-300">
                    Advertising cookies are used to deliver personalized advertisements and track the effectiveness of our marketing campaigns. These cookies may be set by third-party advertising partners.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Third-Party Services</h2>
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                We use the following third-party services that may set cookies:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-600 dark:text-gray-300">
                <li><strong>Google Analytics:</strong> Helps us understand website usage and improve user experience</li>
                <li><strong>Google AdSense:</strong> Serves personalized advertisements (if enabled)</li>
                <li><strong>Microsoft Clarity:</strong> Provides website analytics and user behavior insights</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Managing Cookies</h2>
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                You can manage your cookie preferences at any time using the controls above. Additionally, most web browsers allow you to control cookies through their settings. However, disabling certain cookies may limit your ability to use some features of our website.
              </p>
              <p className="text-gray-600 dark:text-gray-300">
                For more information about how to manage cookies in your browser, please visit:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-600 dark:text-gray-300 mt-2">
                <li><a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline">Google Chrome</a></li>
                <li><a href="https://support.mozilla.org/en-US/kb/enable-and-disable-cookies-website-preferences" target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline">Mozilla Firefox</a></li>
                <li><a href="https://support.apple.com/guide/safari/manage-cookies-and-website-data-sfri11471/mac" target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline">Safari</a></li>
                <li><a href="https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2d9461b0c6b0" target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline">Microsoft Edge</a></li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Contact Us</h2>
              <p className="text-gray-600 dark:text-gray-300">
                If you have any questions about our use of cookies, please contact us at{' '}
                <a href="mailto:info@livelovespain.com" className="text-blue-600 dark:text-blue-400 hover:underline">
                  info@livelovespain.com
                </a>
              </p>
            </section>
          </div>
        </div>
      </div>
    </CRMLayout>
  );
};

export default Cookies;


